import { useCallback } from 'react';

import type { ResolvedExperimentData } from '@change-corgi/experiments/configs';

import { useTrackOnce } from './useTrackOnce';

type Options = Readonly<{
	/**
	 * Reset keys that re-fire the event when changed. Required when the same component instance
	 * is reused across logical contexts (e.g. SPA petition change with no remount), since
	 * variations are deterministic per user and would otherwise dedup the legitimate fire.
	 */
	resetKeys?: readonly unknown[];
	/**
	 * Also fires the legacy `experiment_started` event alongside `$exposure`, for consumers that
	 * still depend on it downstream. New usage should omit this — `$exposure` is the event of
	 * record for Amplitude Experiment.
	 */
	legacy?: boolean;
}>;

/**
 * Returns a function that fires `$exposure` (`flag_key`/`variant`/`variant_name`) for the given
 * experiment, and — when `legacy: true` — also fires `experiment_started` with `experiment_name`
 * and `variant_name`. Each event is deduped independently per mount via `useTrackOnce`. Treated
 * control users fire too (control is a treatment arm for lift analysis).
 *
 * No-ops when:
 * - the experiment is `experiment-service` and `treated === false` — deferred treatment (the GQL
 *   field's `treat: false`), not yet a real assignment;
 * - `experiment.fallback` or `experiment.override` is set — the value isn't a real assignment
 *   (kill-switch, disabled, not-found, sdk-error, bot, QA override, …), so exposure would
 *   misattribute it; or
 * - `experimentName` is empty — e.g. a `growthbook` experiment that matched a rule with no distinct
 *   GrowthBook tracking key (a real assignment, just nothing to attribute `$exposure` to).
 *
 * @example
 * const trackExperimentExposure = useTrackExperimentExposure(experiment);
 * useEffect(() => { void trackExperimentExposure(); }, [trackExperimentExposure]);
 */
export function useTrackExperimentExposure(
	experiment: ResolvedExperimentData,
	{ resetKeys = [], legacy = false }: Options = {},
): () => Promise<boolean> {
	const { experimentName, variation, treated, type, fallback, override } = experiment;

	// `variant_name` is the human-readable variation. `variant_key`/`variant` is what Amplitude
	// Experiment keys assignment on: for growthbook it's the GrowthBook variation key
	// (`experimentResult.key`); other types have no distinct key, so it mirrors the variation.
	//
	// For growthbook we intentionally do NOT fall back to `variation` here: an *unassigned* growthbook
	// experiment has an empty `variantKey` (and empty `experimentName`), and the `if (!experimentName)`
	// guard below drops the event entirely — so an empty key is never emitted. Falling back to
	// `variation` would silently send the display name as the key if that guard ever changed.
	const variantKey = type === 'growthbook' ? experiment.variantKey : variation;

	const trackExposureOnce = useTrackOnce([experimentName, variation, variantKey, ...resetKeys]);
	const trackStartedOnce = useTrackOnce([experimentName, variation, variantKey, ...resetKeys]);

	return useCallback(async (): Promise<boolean> => {
		if (type === 'experiment-service' && !treated) return false;
		if (fallback || override) return false;
		if (!experimentName) return false;

		const exposed = await trackExposureOnce('$exposure', {
			flag_key: experimentName,
			variant: variantKey,
			variant_name: variation,
			experiment_type: type,
		});

		if (legacy) {
			await trackStartedOnce('experiment_started', {
				experiment_name: experimentName,
				variant_name: variation,
				variant_key: variantKey,
			});
		}

		return exposed;
	}, [
		trackExposureOnce,
		trackStartedOnce,
		legacy,
		treated,
		type,
		fallback,
		override,
		experimentName,
		variation,
		variantKey,
	]);
}
